import { NetworkStatus } from "@apollo/client";
import { useCallback, useEffect } from "react";
import { Controller, FormProvider, useForm } from "react-hook-form";

import FormFooterEdit from "components/Forms/FooterEdit";
import MissingDataBanner from "components/MissingDataBanner";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { TERRAFORM_WORKFLOW_TOOL_OPTIONS } from "constants/terraformWorkflowTools";
import { WORKER_POOL_SHARED_VALUE } from "constants/workerPool";
import Box from "ds/components/Box";
import FormField from "ds/components/Form/Field";
import FormToggleField from "ds/components/Form/ToggleField";
import Input from "ds/components/Input";
import Select from "ds/components/Select";
import TooltipModalBody from "ds/components/TooltipModal/Body";
import { TooltipModalTitle } from "ds/components/TooltipModal/Title";
import Typography from "ds/components/Typography";
import useBlockNavigationModal from "hooks/useBlockNavigationModal";
import useTypedContext from "hooks/useTypedContext";
import { useWorkerPoolOptions } from "shared/WorkerPool/useWorkerPoolOptions";
import { Maybe, TerraformWorkflowTool } from "types/generated";
import { withoutPublicWorkerPool } from "utils/distribution";
import { getDocsUrl } from "utils/getDocsUrl";
import { getTooltipAnalyticsProps } from "views/Account/NewModule/utils";

import { ModuleSettingsContext } from "../Context";
import { useUpdateModule } from "../hooks/useUpdateModule";
import { getDefaultValues } from "./utils";

type FormValues = {
  administrative: boolean;
  localPreviewEnabled: boolean;
  protectFromDeletion: boolean;
  workflowTool: TerraformWorkflowTool;
  workerPool: string;
  runnerImage: Maybe<string>;
};

const noPublicWorkerPool = withoutPublicWorkerPool();

const ModuleSettingsBehaviorEditForm = () => {
  const { module } = useTypedContext(ModuleSettingsContext);
  const {
    workerPoolsOptions,
    loading: loadingWorkerPoolOptions,
    hasData,
    refetch,
    networkStatus,
  } = useWorkerPoolOptions(module.spaceDetails.id);

  const { updateModule, loading } = useUpdateModule({ module });

  const builderForm = useForm<FormValues>({
    defaultValues: getDefaultValues(module),
    mode: "onChange",
  });

  const {
    control,
    setValue,
    watch,
    trigger,
    reset,
    handleSubmit,
    register,
    formState: { isDirty, isSubmitSuccessful },
  } = builderForm;

  useBlockNavigationModal({ isNavigationBlocked: isDirty });

  const selectedWorkerPool = watch("workerPool");

  useEffect(() => {
    if (!selectedWorkerPool) {
      if (noPublicWorkerPool && workerPoolsOptions?.length) {
        setValue("workerPool", workerPoolsOptions[0].value, { shouldValidate: true });
      }
    } else if (hasData || (!loadingWorkerPoolOptions && !hasData)) {
      // validate worker pool shared via link
      const isNotAvailable = !workerPoolsOptions.find(({ value }) => {
        return value === selectedWorkerPool;
      });
      if (isNotAvailable) {
        setValue("workerPool", "");
      }
    }
  }, [workerPoolsOptions, setValue, selectedWorkerPool, hasData, loadingWorkerPoolOptions]);

  const handleWorkerPoolChange = (workerPool: string) => {
    setValue("workerPool", workerPool, { shouldValidate: true, shouldDirty: true });
  };

  const handleWorkflowToolChange = useCallback(
    (value: TerraformWorkflowTool) => {
      setValue("workflowTool", value, { shouldDirty: true });

      trigger();
    },
    [setValue, trigger]
  );

  const onSubmit = useCallback(
    () =>
      handleSubmit((values: FormValues) => {
        return updateModule({
          ...values,
          workerPool: values.workerPool === WORKER_POOL_SHARED_VALUE ? null : values.workerPool,
          runnerImage: values.runnerImage || null,
        });
      })(),
    [handleSubmit, updateModule]
  );

  useEffect(() => {
    if (isSubmitSuccessful) {
      reset(getDefaultValues(module));
    }
  }, [isSubmitSuccessful, module, reset]);

  return (
    <FormProvider {...builderForm}>
      <Box direction="column" gap="large">
        <Controller
          name="workerPool"
          rules={{
            required: "Worker pool is required",
          }}
          control={control}
          render={({ field, fieldState }) => (
            <>
              <FormField
                label="Worker pool"
                error={fieldState.error?.message}
                tooltipInfoVariant="modal"
                tooltipInfo={
                  <>
                    <TooltipModalTitle>Worker pool</TooltipModalTitle>
                    <TooltipModalBody align="start">
                      <Typography tag="p" variant="p-body3">
                        By using a private worker pool you make sure that you have full control over
                        your infrastructure changes, and can even access resources that would
                        otherwise not be reachable from the public internet.
                      </Typography>
                      <ReadMoreDocsLink docsUrl={getDocsUrl("/concepts/worker-pools")} />
                    </TooltipModalBody>
                  </>
                }
                {...getTooltipAnalyticsProps("Behavior", "Worker pool")}
                noMargin
                fullWidth
              >
                {({ ariaInputProps }) => (
                  <Select
                    isDisabled={workerPoolsOptions.length === 1}
                    value={field.value}
                    items={workerPoolsOptions}
                    onChange={handleWorkerPoolChange}
                    placeholder="Select worker pool"
                    isError={!!fieldState.error}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
              {!loadingWorkerPoolOptions && !hasData && (
                <Box direction="column" margin="large 0 0 0">
                  <MissingDataBanner
                    text="Couldn't load your private worker pools, please refresh or come back later"
                    refreshHandler={refetch}
                    refreshLoading={loading && networkStatus === NetworkStatus.refetch}
                  />
                </Box>
              )}
            </>
          )}
        />
        <Controller
          name="workflowTool"
          control={control}
          render={({ field, fieldState }) => (
            <FormField
              label="Workflow tool"
              error={fieldState.error?.message}
              tooltipInfoVariant="modal"
              tooltipInfo={
                <>
                  <TooltipModalTitle>Workflow tool</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    The tool used to execute the workflow commands. This can be an open source
                    (FOSS) version of OpenTofu, Terraform or a custom tool.
                  </TooltipModalBody>
                </>
              }
              noMargin
              fullWidth
            >
              {({ ariaInputProps }) => (
                <Select
                  value={field.value as TerraformWorkflowTool}
                  items={TERRAFORM_WORKFLOW_TOOL_OPTIONS}
                  onChange={handleWorkflowToolChange}
                  isError={!!fieldState.error}
                  {...ariaInputProps}
                />
              )}
            </FormField>
          )}
        />
        <FormField
          label="Runner image"
          isOptional
          noMargin
          tooltipInfo={
            <>
              <TooltipModalTitle>Runner image</TooltipModalTitle>
              <TooltipModalBody align="start">
                <Typography tag="p" variant="p-body3">
                  Optional runner image used to process the module metadata during parsing Run.
                </Typography>
                <ReadMoreDocsLink
                  docsUrl={getDocsUrl("/concepts/stack/stack-settings#runner-image")}
                />
              </TooltipModalBody>
            </>
          }
          tooltipInfoVariant="modal"
        >
          {({ ariaInputProps }) => (
            <Input
              placeholder="Runner image"
              {...register("runnerImage", {
                setValueAs: (value: string) => value?.trim(),
              })}
              {...ariaInputProps}
            />
          )}
        </FormField>

        <Controller
          name="administrative"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Administrative"
              description="Module will receive a runtime environment variable giving administrative access to other stacks within the same account"
              tooltipInfo={
                <>
                  <TooltipModalTitle>Administrative module</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <Typography tag="p" variant="p-body3">
                      Administrative modules can create, update and destroy Spacelift resources.
                    </Typography>
                    <ReadMoreDocsLink
                      docsUrl={getDocsUrl("/concepts/stack/stack-settings#administrative")}
                    />
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />

        <Controller
          name="localPreviewEnabled"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Enable local preview"
              description="Run local previews using spacectl."
              tooltipInfo={
                <>
                  <TooltipModalTitle>Local preview</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <Typography tag="p" variant="p-body3">
                      When set to true, proposed runs can be created based on user-uploaded local
                      workspaces. This way you can preview how your code changes will execute
                      without creating a commit.
                    </Typography>
                    <ReadMoreDocsLink
                      docsUrl={getDocsUrl("/concepts/stack/stack-settings#enable-local-preview")}
                    />
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />
        <Controller
          name="protectFromDeletion"
          control={control}
          render={({ field }) => (
            <FormToggleField
              variant="switch"
              onChange={field.onChange}
              checked={field.value}
              title="Protect from deletion (recommended)"
              description="Manage deletion protection"
              tooltipInfo={
                <>
                  <TooltipModalTitle>Protect from deletion</TooltipModalTitle>
                  <TooltipModalBody align="start">
                    <Typography tag="p" variant="p-body3">
                      Deletion protection helps protect your stacks from accidental deletion. When
                      enabled, any attempts to delete your stack will fail.
                    </Typography>
                  </TooltipModalBody>
                </>
              }
            />
          )}
        />
      </Box>
      <FormFooterEdit
        onSubmit={onSubmit}
        onCancel={() => reset(getDefaultValues(module))}
        isDirty={isDirty}
        submitLoading={loading}
        margin="large 0 0"
      />
    </FormProvider>
  );
};

export default ModuleSettingsBehaviorEditForm;
